import { useState } from 'react';
import { PROMO_CODE_MAX_LENGTH } from '@affily/shared/constants';
import { isValidPromoCode, normalizePromoCode } from '@affily/shared/promoCodes';
import { api } from '../lib/api.js';
import { TrackingLink } from './TrackingLink.jsx';
import { Button, Badge, Input } from './ui.jsx';

/**
 * A partnership's creator code: the code, what it gives a shopper, and — for
 * the brand — whether it exists in their store yet, plus a rename.
 *
 * Renders as a fragment for the caller's flex row, like TrackingLink.
 */
export function CreatorCode({ partnership, side, onError, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(partnership.promoCode ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!partnership.promoCode) return null;

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.patch(`/partnerships/${partnership.id}/code`, { code: draft });
      setEditing(false);
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    const normalized = normalizePromoCode(draft);
    return (
      <form onSubmit={save} className="flex flex-wrap items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          maxLength={PROMO_CODE_MAX_LENGTH}
          aria-label="Creator code"
          className="w-40 font-mono uppercase"
          autoFocus
        />
        <Button size="sm" type="submit" disabled={busy || !isValidPromoCode(normalized)}>
          Save
        </Button>
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(partnership.promoCode);
            setError('');
          }}
        >
          Cancel
        </Button>
        <span
          className={`text-xs ${error ? 'text-negative' : 'text-ink-faint'}`}
          role={error ? 'alert' : undefined}
        >
          {error || '3–20 letters and digits. Create the new code in your store too.'}
        </span>
      </form>
    );
  }

  return (
    <>
      <TrackingLink url={partnership.promoCode} onError={onError} noun="code" />
      {partnership.codeDiscount && (
        <span className="text-xs text-ink-faint">{partnership.codeDiscount} for shoppers</span>
      )}
      {side === 'brand' && partnership.promoCodeSync === 'manual' && (
        <Badge tone="caution" title="Create this discount code in your store so shoppers can use it">
          Create in your store
        </Badge>
      )}
      {side === 'brand' && partnership.promoCodeSync === 'failed' && (
        <Badge tone="negative">Store sync failed</Badge>
      )}
      {side === 'brand' && (
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          Rename
        </Button>
      )}
    </>
  );
}

export default CreatorCode;
